import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Plus, ShoppingBag, Trash2 } from 'lucide-react';

import { PageHeader } from '../../../../components/ui/PageHeader';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { notify } from '../../../../features/feedback/notify';

import {
  fetchEcommerceIntegrations,
  fetchEcommerceWarehouses,
  deleteEcommerceIntegration,
} from '../../../../features/ecommerces/ecommercesThunks';
import { setEcommerceFilters } from '../../../../features/ecommerces/ecommercesSlice';
import { ECOMMERCE_NOTICE } from '../../../../features/ecommerces/ecommercesFeedback';
import {
  selectFilteredIntegrations,
  selectWarehouseNames,
} from '../../../../features/ecommerces/ecommercesSelectors';
import {
  getConnectionStatus,
  getPlatformDisplayName,
} from '../../../../features/ecommerces/ecommercesPlatforms';
import { getOauthReturnNotice, resolveOauthReturn } from '../../../../features/ecommerces/oauthReturn';

import AddEcommerceModal from './components/AddEcommerceModal';
import EcommerceFilters from './components/EcommerceFilters';
import EcommerceStatusBadge from './components/EcommerceStatusBadge';

const ECOMMERCE_LIST_PATH = '/configuration/ecommerce';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-CL') : '-');

/**
 * `/configuration/ecommerce`: the seller's integrations. It is also where the
 * backend's Shopify and Mercado Libre callbacks send the seller back, so on load
 * it forwards a return to the integration that was being connected.
 */
const EcommerceConfiguration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();

  const { integrations, loading, error, filters } = useSelector((state) => state.ecommerces);
  const filtered = useSelector(selectFilteredIntegrations);
  const warehouseNames = useSelector(selectWarehouseNames);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Whether this visit's own request came back. A return from OAuth has to be
  // read against that listing, the one holding the record the init created —
  // not against whatever the store kept from before.
  const [fetched, setFetched] = useState(false);
  // The return is read once. StrictMode runs effects twice in development, and
  // a second read, with the mark already consumed, would undo the first
  // navigation by sending the seller back to the listing.
  const returnHandledRef = useRef(false);

  useEffect(() => {
    let active = true;
    dispatch(fetchEcommerceIntegrations()).finally(() => {
      if (active) setFetched(true);
    });
    dispatch(fetchEcommerceWarehouses());
    return () => {
      active = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!fetched || returnHandledRef.current) return;
    returnHandledRef.current = true;

    const target = resolveOauthReturn({ search, integrations });
    const returnNotice = getOauthReturnNotice(search);
    // Asked from a warehouse screen: the seller goes back there, told how it went.
    if (target?.returnTo) {
      if (returnNotice) dispatch(notify[returnNotice.type](returnNotice.message, { testId: ECOMMERCE_NOTICE }));
      navigate(target.returnTo, { replace: true });
      return;
    }
    if (target) {
      navigate(`${ECOMMERCE_LIST_PATH}/${target.id}${search}`, { replace: true });
      return;
    }

    if (returnNotice) {
      dispatch(notify[returnNotice.type](returnNotice.message, { testId: ECOMMERCE_NOTICE }));
      navigate(ECOMMERCE_LIST_PATH, { replace: true });
    }
  }, [fetched, integrations, search, navigate, dispatch]);

  const handleDelete = async () => {
    setDeleting(true);
    await dispatch(deleteEcommerceIntegration(toDelete.id));
    setDeleting(false);
    setToDelete(null);
  };

  const lastPage = Math.max(1, Math.ceil(filtered.length / filters.rowsPerPage));
  const page = Math.min(filters.page, lastPage);
  const rows = filtered.slice((page - 1) * filters.rowsPerPage, page * filters.rowsPerPage);

  const columns = [
    {
      header: 'INTEGRACIÓN',
      cell: (row) => (
        <div className="flex items-center gap-3">
          {row.image ? (
            <img
              src={row.image}
              alt=""
              className="w-9 h-9 object-contain bg-white rounded-lg p-1 border border-border"
            />
          ) : (
            <div className="w-9 h-9 rounded-lg border border-border bg-ninja-gray dark:bg-[#2d2d2d]" />
          )}
          <span className="font-semibold">{getPlatformDisplayName(row.name)}</span>
        </div>
      ),
    },
    {
      header: 'BODEGA',
      cell: (row) => warehouseNames[String(row.warehouse)] || `Bodega #${row.warehouse}`,
    },
    { header: 'ESTADO', cell: (row) => <EcommerceStatusBadge status={getConnectionStatus(row)} /> },
    { header: 'CREADA', cell: (row) => formatDate(row.created_at) },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Eye}
            aria-label={`Ver detalle de ${getPlatformDisplayName(row.name)}`}
            title="Ver detalle"
            onClick={() => navigate(`${ECOMMERCE_LIST_PATH}/${row.id}`)}
          />
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Trash2}
            aria-label={`Eliminar ${getPlatformDisplayName(row.name)}`}
            title="Eliminar"
            className="hover:text-danger"
            onClick={() => setToDelete(row)}
          />
        </div>
      ),
    },
  ];

  return (
    <div data-testid="ecommerce-page" className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={ShoppingBag}
        iconClassName="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
        title="Integraciones E-commerce"
        description="Conecta tus tiendas para recibir sus órdenes en NinjaHubs."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setIsAddOpen(true)}
            data-testid="ecommerce-add"
          >
            Agregar integración
          </Button>
        }
      />

      <EcommerceFilters />

      <DataTable
        columns={columns}
        data={rows}
        totalItems={filtered.length}
        currentPage={page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchEcommerceIntegrations())}
        onPageChange={(nextPage) => dispatch(setEcommerceFilters({ page: nextPage }))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setEcommerceFilters({ rowsPerPage }))}
        itemLabel="integraciones"
        emptyMessage={
          integrations.length === 0
            ? 'Aún no tienes integraciones. Usa "Agregar integración" para conectar tu tienda.'
            : 'Ninguna integración coincide con los filtros.'
        }
      />

      <AddEcommerceModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />

      <ConfirmDialog
        isOpen={Boolean(toDelete)}
        onClose={() => !deleting && setToDelete(null)}
        onConfirm={handleDelete}
        title="Eliminar integración"
        message={
          toDelete
            ? `Se elimina la integración de ${getPlatformDisplayName(toDelete.name)}. Dejarás de recibir sus órdenes en NinjaHubs.`
            : ''
        }
        loading={deleting}
        testId="ecommerce-delete-dialog"
      />
    </div>
  );
};

export default memo(EcommerceConfiguration);
